import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import type { MappingFieldChecks } from '../types'
import type { ColumnMapping, MappingField } from '../types/launch'
import { buttonClass, inputClass } from './ui'

type Props = {
  headers: string[]
  fields: MappingField[]
  suggestions: ColumnMapping
  field_checks: MappingFieldChecks
  processing: boolean
  error?: string
  onSubmit: (mapping: ColumnMapping) => void
}

function validUnit(field: MappingField, suggested?: string | null): string | null {
  if (field.units.length === 1) return field.units[0]
  return suggested && field.units.includes(suggested) ? suggested : null
}

export default function MappingForm({ headers, fields, suggestions, field_checks, processing, error, onSubmit }: Props) {
  const id = useId()
  const clubSelect = useRef<HTMLSelectElement>(null)
  const touched = useRef<Record<string, 'Edited' | 'Confirmed'>>({})
  const [actions, setActions] = useState<Record<string, 'Edited' | 'Confirmed'>>({})
  const unitSelects = useRef<Record<string, HTMLSelectElement | null>>({})
  const [unitErrors, setUnitErrors] = useState<string[]>([])
  const [clubError, setClubError] = useState(false)
  const [choices, setChoices] = useState<ColumnMapping>(() => Object.fromEntries(fields.map(field => [field.key, {
    column: suggestions[field.key]?.column ?? '',
    unit: validUnit(field, suggestions[field.key]?.unit),
  }])))

  useEffect(() => {
    setChoices(previous => {
      const next = { ...previous }
      for (const field of fields) {
        if (!touched.current[field.key]) {
          next[field.key] = { column: suggestions[field.key]?.column ?? '', unit: validUnit(field, suggestions[field.key]?.unit) }
        }
      }
      return next
    })
  }, [suggestions, fields])

  function touch(key: string, action: 'Edited' | 'Confirmed') {
    touched.current[key] = action
    setActions(previous => ({ ...previous, [key]: action }))
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    if (processing) return
    if (!choices.club?.column) {
      setClubError(true)
      clubSelect.current?.focus()
      return
    }
    const missingUnits = fields.filter(field => choices[field.key]?.column && field.units.length > 1 && !validUnit(field, choices[field.key]?.unit)).map(field => field.key)
    setUnitErrors(missingUnits)
    if (missingUnits.length) {
      unitSelects.current[missingUnits[0]]?.focus()
      return
    }
    const mapping: ColumnMapping = {}
    for (const field of fields) {
      const choice = choices[field.key]
      if (choice?.column) {
        mapping[field.key] = field.dimension === 'text' ? { column: choice.column } : { column: choice.column, unit: validUnit(field, choice.unit) }
      }
    }
    onSubmit(mapping)
  }

  return (
    <form onSubmit={submit} aria-label="Column mapping" aria-busy={processing} className="overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line p-6 sm:px-8">
        <h2 className="font-serif text-2xl">Match the measurements</h2>
        <p className="mt-3 max-w-2xl text-sm leading-7 text-muted">Choose the column for each measurement, then check its units. Leave measurements that aren’t in your file unmatched.</p>
        {error && <p role="alert" className="mt-4 rounded-md border border-alert-border bg-alert p-3 text-sm text-alert-ink">{error}</p>}
      </div>
      <fieldset disabled={processing} className="divide-y divide-line">
        <legend className="sr-only">CSV columns and units</legend>
        {fields.map(field => {
          const columnId = `${id}-${field.key}-column`
          const unitId = `${id}-${field.key}-unit`
          const check = actions[field.key] ? undefined : field_checks[field.key]
          const review = check?.status === 'check' || (check?.status === 'rejected' && check.source === 'remembered')
          const reasonsId = `${columnId}-reasons`
          const hasReasons = !!check?.reasons.length
          const unitInvalid = unitErrors.includes(field.key) && !!choices[field.key]?.column && !validUnit(field, choices[field.key]?.unit)
          const badge = actions[field.key] ?? (check?.status === 'rejected' && check.source !== 'remembered' ? 'Not suggested' : check ? { matched: 'Matched', ai: 'Suggested by AI', remembered: 'Remembered' }[check.source] : 'Not suggested')
          const invalid = field.key === 'club' && clubError
          return (
            <div key={field.key} data-review={review ? "true" : "false"} className={`grid gap-4 px-6 py-5 sm:grid-cols-[1.5fr_1fr] sm:px-8 ${review ? "bg-notice ring-1 ring-inset ring-notice-border" : ""}`}>
              <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
                <span className="rounded-full border border-line px-2 py-1 text-xs text-muted">{badge}</span>
                {review && <><span className="text-sm font-medium text-notice-ink">{check?.status === 'rejected' ? 'Review saved mapping' : 'Check this'}</span><button type="button" className="min-h-10 rounded-md px-3 text-sm underline focus-visible:outline-2 focus-visible:outline-brand" onClick={() => touch(field.key, 'Confirmed')}>Confirm {field.label}</button></>}
              </div>
              <div className="space-y-2">
                <label htmlFor={columnId} className="block text-sm font-medium">{field.label} column</label>
                <select id={columnId} ref={field.key === 'club' ? clubSelect : undefined} className={inputClass}
                  value={choices[field.key]?.column ?? ''} aria-invalid={invalid ? true : undefined} aria-describedby={[invalid ? `${columnId}-error` : '', hasReasons ? reasonsId : ''].filter(Boolean).join(' ') || undefined}
                  onChange={event => {
                    const column = event.target.value
                    touch(field.key, 'Edited')
                    setChoices(previous => ({ ...previous, [field.key]: { column, unit: column === previous[field.key]?.column ? previous[field.key]?.unit : validUnit(field) } }))
                    if (field.key === 'club' && column) setClubError(false)
                  }}>
                  <option value="">Not in this file</option>
                  {headers.map(header => <option key={header} value={header}>{header}</option>)}
                </select>
                {invalid && <p id={`${columnId}-error`} role="alert" className="text-sm text-alert-ink">Choose the column that holds the club.</p>}
              </div>
              {field.units.length > 1 ? (
                <div className="space-y-2">
                  <label htmlFor={unitId} className="block text-sm font-medium">{field.label} unit</label>
                  <select id={unitId} className={inputClass} value={choices[field.key]?.unit ?? ''}
                    ref={element => { unitSelects.current[field.key] = element }} aria-invalid={unitInvalid || undefined}
                    aria-describedby={[unitInvalid ? `${unitId}-error` : '', hasReasons ? reasonsId : ''].filter(Boolean).join(' ') || undefined}
                    onChange={event => {
                      const unit = event.target.value
                      touch(field.key, 'Edited')
                      setChoices(previous => ({ ...previous, [field.key]: { column: previous[field.key]?.column ?? '', unit } }))
                    }}>
                    <option value="">Choose unit</option>
                    {field.units.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                  </select>
                  {unitInvalid && <p id={`${unitId}-error`} role="alert" className="text-sm text-alert-ink">Choose a unit for {field.label}.</p>}
                </div>
              ) : (
                <p className="self-center text-sm text-muted sm:pt-6">{field.dimension === 'text' ? 'Required · identifies the club for each shot' : `Unit: ${validUnit(field) ?? 'unitless'}`}</p>
              )}
              {hasReasons && <ul id={reasonsId} className="space-y-1 text-sm text-notice-ink sm:col-span-2">{check.reasons.map((reason, index) => <li key={index}>{reason}</li>)}</ul>}
            </div>
          )
        })}
      </fieldset>
      <div className="flex flex-col justify-between gap-4 border-t border-line bg-paper p-6 sm:flex-row sm:items-center sm:px-8">
        <p className="max-w-md text-xs leading-6 text-muted">Check the units in your export. Results are shown in mph, yards, degrees and rpm.</p>
        <button type="submit" className={`${buttonClass} shrink-0`} disabled={processing}>{processing ? 'Importing shots…' : 'Import shots'}</button>
      </div>
    </form>
  )
}
